import { useState, useEffect } from 'react';
import { useOutletContext } from 'react-router-dom';
import { adminApi } from '../../services/adminApi';
import { UserCog, Loader2, Plus, X, Mail, Shield } from 'lucide-react';
import { toast } from 'sonner';

const ROLE_COLORS = {
  'Super Admin': 'bg-purple-500/10 text-purple-400 border-purple-500/20',
  'Branch Manager': 'bg-blue-500/10 text-blue-400 border-blue-500/20',
  'Operations Manager': 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20',
  'Mover': 'bg-amber-500/10 text-amber-400 border-amber-500/20',
  'Intern': 'bg-cyan-500/10 text-cyan-400 border-cyan-500/20',
};

export default function Team() {
  const ctx = useOutletContext();
  const [team, setTeam] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({ full_name: '', email: '', role: 'Branch Manager', school: '' });

  useEffect(() => {
    adminApi.getTeam().then(d => { setTeam(d); setLoading(false); }).catch(() => setLoading(false));
  }, []);

  if (loading) return <div className="flex items-center justify-center h-64"><Loader2 className="w-8 h-8 animate-spin text-blue-400" /></div>;

  const addMember = async () => {
    if (!form.full_name || !form.email) { toast.error('Name and email required'); return; }
    try {
      const newMember = await adminApi.addTeamMember(form);
      setTeam(prev => [...prev, newMember]);
      setShowAdd(false);
      setForm({ full_name: '', email: '', role: 'Branch Manager', school: '' });
      toast.success('Team member added');
    } catch {
      toast.error('Failed to add team member');
    }
  };

  const roleCounts = team.reduce((acc, m) => { acc[m.role] = (acc[m.role] || 0) + 1; return acc; }, {});

  return (
    <div data-testid="admin-team">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold" style={{ fontFamily: 'Chivo, sans-serif' }}>Team Management</h1>
          <p className={`text-sm ${ctx.textSecondary} mt-1`}>{team.length} team members</p>
        </div>
        <button onClick={() => setShowAdd(true)} className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700" data-testid="add-member-btn">
          <Plus className="w-4 h-4" /> Add Member
        </button>
      </div>

      {/* Role summary */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 mb-6">
        {Object.entries(roleCounts).map(([role, count]) => (
          <div key={role} className={`${ctx.cardBg} rounded-xl border ${ctx.borderColor} p-4`}>
            <Shield className="w-4 h-4 mb-2" style={{ color: ROLE_COLORS[role]?.includes('purple') ? '#a855f7' : ROLE_COLORS[role]?.includes('blue') ? '#3b82f6' : '#10b981' }} />
            <p className="text-xl font-bold">{count}</p>
            <p className={`text-xs ${ctx.textSecondary}`}>{role}</p>
          </div>
        ))}
      </div>

      {/* Team table */}
      <div className={`${ctx.cardBg} rounded-xl border ${ctx.borderColor} overflow-x-auto`}>
        <table className="w-full text-sm" data-testid="team-table">
          <thead><tr className={`border-b ${ctx.borderColor}`}>
            {['Member', 'Email', 'Role', 'Branch / School', 'Status'].map(h => (
              <th key={h} className={`text-left px-4 py-3 text-xs font-medium ${ctx.textSecondary} uppercase tracking-wider`}>{h}</th>
            ))}
          </tr></thead>
          <tbody>{team.map(m => (
            <tr key={m._id} className={`border-b ${ctx.borderColor} ${ctx.hoverBg}`}>
              <td className="px-4 py-3">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center text-white text-xs font-bold">
                    {m.full_name.split(' ').map(n => n[0]).join('')}
                  </div>
                  <span className="font-medium text-sm">{m.full_name}</span>
                </div>
              </td>
              <td className="px-4 py-3 text-xs">{m.email}</td>
              <td className="px-4 py-3"><span className={`text-xs px-2 py-0.5 rounded-full border ${ROLE_COLORS[m.role] || ''}`}>{m.role}</span></td>
              <td className="px-4 py-3 text-xs">{m.school}</td>
              <td className="px-4 py-3"><span className="text-xs px-2 py-0.5 rounded-full bg-emerald-500/10 text-emerald-400">{m.status}</span></td>
            </tr>
          ))}</tbody>
        </table>
      </div>

      {/* Add modal */}
      {showAdd && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setShowAdd(false)}>
          <div className={`${ctx.cardBg} rounded-2xl border ${ctx.borderColor} max-w-md w-full p-6`} onClick={e => e.stopPropagation()} data-testid="add-member-modal">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold">Add Team Member</h3>
              <button onClick={() => setShowAdd(false)} className={`p-1 rounded-lg ${ctx.hoverBg}`}><X className="w-4 h-4" /></button>
            </div>
            <div className="space-y-3">
              <div>
                <label className={`text-xs ${ctx.textSecondary} block mb-1`}>Full Name</label>
                <input value={form.full_name} onChange={e => setForm({ ...form, full_name: e.target.value })}
                  data-testid="add-member-name"
                  className={`w-full px-3 py-2 rounded-lg text-sm border ${ctx.borderColor} bg-transparent outline-none focus:ring-2 focus:ring-blue-500/30`} />
              </div>
              <div>
                <label className={`text-xs ${ctx.textSecondary} block mb-1`}>Email</label>
                <input value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} type="email"
                  data-testid="add-member-email"
                  className={`w-full px-3 py-2 rounded-lg text-sm border ${ctx.borderColor} bg-transparent outline-none focus:ring-2 focus:ring-blue-500/30`} />
              </div>
              <div>
                <label className={`text-xs ${ctx.textSecondary} block mb-1`}>Role</label>
                <select value={form.role} onChange={e => setForm({ ...form, role: e.target.value })}
                  data-testid="add-member-role"
                  className={`w-full px-3 py-2 rounded-lg text-sm border ${ctx.borderColor} bg-transparent outline-none`}>
                  {Object.keys(ROLE_COLORS).map(r => <option key={r} value={r}>{r}</option>)}
                </select>
              </div>
              <div>
                <label className={`text-xs ${ctx.textSecondary} block mb-1`}>Branch / School</label>
                <input value={form.school} onChange={e => setForm({ ...form, school: e.target.value })}
                  data-testid="add-member-school"
                  className={`w-full px-3 py-2 rounded-lg text-sm border ${ctx.borderColor} bg-transparent outline-none focus:ring-2 focus:ring-blue-500/30`} />
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-5">
              <button onClick={() => setShowAdd(false)} className={`px-3 py-1.5 rounded-lg text-xs ${ctx.hoverBg}`}>Cancel</button>
              <button onClick={addMember} data-testid="add-member-submit" className="px-3 py-1.5 rounded-lg bg-blue-600 text-white text-xs">Add Member</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
